<template>
  <BaseModal :modalTitle="$t('area.dhl.credentials.title')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="accountNumber"
        :label="$t('area.dhl.credentials.field.account-number')"
        :rules="['required']"
        isRequired
        v-model="form.accountNumber" />
      <UiInputTextFieldVee
        name="apiUsername"
        :label="$t('area.dhl.credentials.field.api-username')"
        :rules="['required']"
        isRequired
        v-model="form.apiUsername" />
      <div>
        <UiInputTextFieldVee
          name="apiPassword"
          type="password"
          :label="$t('area.dhl.credentials.field.api-password')"
          :rules="isEditing ? [] : ['required']"
          :isRequired="!isEditing"
          v-model="form.apiPassword" />
        <p
          v-if="isEditing"
          class="mt-1 text-xs text-text-secondary">
          {{ $t('area.dhl.credentials.hint.leave-blank') }}
        </p>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isSaving"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'DhlCredentialsModal',

  emits: ['close'],

  props: {
    accountNumber: { type: String, default: '' },
    apiUsername: { type: String, default: '' },
    isEditing: { type: Boolean, default: false },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        accountNumber: this.accountNumber,
        apiUsername: this.apiUsername,
        apiPassword: '',
      },
      isSaving: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
  },

  methods: {
    ...mapActions(useAccountStore, ['updateDhlSettings']),

    async onSubmit() {
      this.isSaving = true
      this.errorText = ''
      try {
        const params: Parameters<typeof this.updateDhlSettings>[0] = {
          accountNumber: this.form.accountNumber,
          apiUsername: this.form.apiUsername,
        }
        if (this.form.apiPassword) {
          params.apiPassword = this.form.apiPassword
        }
        await this.updateDhlSettings(params)
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('area.dhl.credentials.message.saved'))
        this.$emit('close')
      } catch (error: unknown) {
        const code = error instanceof Error ? error.message : ''
        if (code === 'invalid-credentials') {
          this.errorText = this.$t('area.dhl.credentials.error.invalid')
        } else if (code === 'invalid-params') {
          this.errorText = this.$t('area.dhl.credentials.error.account-number-invalid')
        } else {
          this.errorText = this.$t('error.common.standard')
        }
        useToast().error(this.errorText)
      } finally {
        this.isSaving = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
